import { Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';

import { rem, remToGapMultiplier, Theme } from '../../theme';
import { Icon } from '../Icon';

import {
    StyledIconProperties,
    StyledCollapsibleProperties,
} from './PartialCollapsible.types';

export const StyledCollapsible = styled(Collapse, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})<StyledCollapsibleProperties>`
    ${({
        theme,
        $minHeightMobile,
        $minHeightDesktop,
        $alignOnDesktop,
        $isOpen,
    }: StyledCollapsibleProperties & { theme: Theme }) => `
        min-height: ${theme.tggSpacing(rem($minHeightMobile), $minHeightMobile * remToGapMultiplier)} !important;
        margin: ${theme.tggSpacing([0, 'auto'], [0, 'auto'])};
        line-height: ${theme.tggSpacing(rem(2.1), 5.25)};

        ${theme.breakpoints.up('desktop')} {
            min-height: ${theme.tggSpacing(rem($minHeightDesktop), $minHeightDesktop * remToGapMultiplier)} !important;
        }

        ${
            $isOpen
                ? 'mask-image: none'
                : 'mask-image: linear-gradient(to bottom, black 60%, transparent 100%)'
        };

        ${theme.breakpoints.up('desktop')} {
            ${`text-align: ${$alignOnDesktop}`};
        }

        &.MuiCollapse-hidden {
            visibility: visible;
        }
	`}
`;

export const StyledCollapsibleControl = styled('div')<{
    $fullWidth?: boolean;
}>`
    ${({ theme, $fullWidth }: { theme: Theme; $fullWidth?: boolean }) => `
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;
        padding: ${
            $fullWidth
                ? theme.tggSpacing(0, 0)
                : theme.tggSpacing([0, rem(3)], [0, 8])
        };
        margin: ${theme.tggSpacing([rem(1), 'auto', 0], [2.5, 'auto', 0])};

        ${theme.breakpoints.up('desktop')} {
            padding: ${theme.tggSpacing(0, 0)};
        }
	`}
`;

export const StyledDivider = styled('div')<{ $variant?: boolean }>`
    ${({ theme, $variant }: { theme: Theme; $variant?: boolean }) => `
        flex-grow: 1;
        height: ${theme.tggSpacing(rem(0.1), 0.25)};
        background: ${$variant ? theme.palette.common.white : theme.getRebrandingColor(theme.palette.primary.main, theme.palette.tgg.border.action.main)};
	`}
`;

export const StyledButton = styled('button')<{
    $variant?: boolean;
    $rotated?: boolean;
}>`
    ${({
        theme,
        $variant,
        $rotated,
    }: {
        theme: Theme;
        $variant?: boolean;
        $rotated?: boolean;
    }) => `
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: ${theme.tggSpacing(rem(3.5), 12)};
        height: ${theme.tggSpacing(rem(3.5), 12)};
        margin-right: ${theme.tggSpacing(rem(1), 2.5)};
        margin-bottom: ${theme.tggSpacing(rem(0.5), 1.25)};
        margin-left: ${theme.tggSpacing(rem(1), 2.5)};
        cursor: pointer;
        background: none;
        border: ${theme.tggSpacing(rem(0.2), 0.5)} solid ${
            $variant
                ? theme.palette.common.white
                : theme.getRebrandingColor(
                      theme.palette.primary.main,
                      theme.palette.tgg.border.action.main,
                  )
        };
        border-radius: 50%;
        padding: ${theme.tggSpacing(rem(0.375), 0)}
        &:focus,
        &:focus-visible {
            outline: none;
        }
        ${theme.breakpoints.up('desktop')} {
            width: ${theme.tggSpacing(rem(4.5), 12)};
            height: ${theme.tggSpacing(rem(4.5), 12)};
            margin-right: ${theme.tggSpacing(rem(3), 4)};
            margin-left: ${theme.tggSpacing(rem(3), 4)};
        }
    `}
`;

export const StyledChevronIcon = styled(Icon, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})<StyledIconProperties>`
    cursor: pointer;
    transition: all 0.3s;
    transform: ${({ $rotated, theme }) =>
        $rotated
            ? `rotate(${theme.isRebranding('-90deg', '180deg')})`
            : `rotate(${theme.isRebranding('90deg', '0deg')})`};
`;
